<script lang="ts">
  import moment from "moment";
  import { onDestroy, onMount } from "svelte";
  import { get, writable } from "svelte/store";
  import { navigate } from "../../app.logic";
  import { user } from "../../app.store";
  import Button from "../../components/Button.svelte";
  import DebouncedInput from "../../components/Form/Fields/DebouncedInput.svelte";
  import type { IActivity } from "../../entities/activity";
  import type { IPortalUserDTO } from "../../services/DTO/portal-user-dto";
  import type { IUserTimerStatusDTO } from "../../services/DTO/user-timer-status-dto";
  import { PortalUserService } from "../../services/portal-user.service";
  import { UserTimersService } from "../../services/user-timers.service";
  import type { IApiResponse } from "../../utils/api/api-response";
  import { load } from "./timer.store";
  import { createTimerActivity, deleteTimer, updateTimer } from "./timer.store";

  export let timerId = 0;

  let timerInitialized = false;
  let secondInterval = null;
  //let minuteInterval = null;

  const timer = writable<IUserTimerStatusDTO>({
    timerId: 0,
    description: "",
    elapsedSeconds: 0,
    running: false,
    completed: false,
    activityId: 0,
  });

  function formatTime(t: IUserTimerStatusDTO) {
    let ss = t.elapsedSeconds;

    const hh = Math.floor(ss / (60 * 60));
    ss -= hh * 60 * 60;
    const mm = Math.floor(ss / 60);
    ss -= mm * 60;

    let x = hh + ":";
    x += (mm < 10 ? "0" : "") + mm + ":";
    x += (ss < 10 ? "0" : "") + ss;
    return x;
  }

  async function init() {
    if (!timerInitialized) {
      timerInitialized = true;
      await refresh();
      secondInterval = setInterval(periodicSecond, 1000);
      //minuteInterval = setInterval(periodicMinute, 60 * 1000);
    }
  }

  async function halt() {
    if (secondInterval != null) {
      clearInterval(secondInterval);
      secondInterval = null;
    }
    // if (minuteInterval != null) {
    //  clearInterval(minuteInterval);
    //  minuteInterval = null;
    // }

    timerInitialized = false;
  }

  async function refresh() {
    let res = await UserTimersService.fetchUserTimerStatus(timerId);
    timer.set(res.payload);
  }

  async function play() {
    let res = await UserTimersService.playUserTimer(timerId);
    //timer.set(res.payload);
    //refresh();
    // so that other timers get reloaded
    load();
  }

  async function pause() {
    let res = await UserTimersService.pauseUserTimer(timerId);
    //timer.set(res.payload);
    refresh();
  }

  async function stop() {
    let res = await UserTimersService.stopUserTimer(timerId);
    //timer.set(res.payload);
    refresh();
  }

  async function periodicSecond() {
    const t = get(timer);
    if (t.running) {
      t.elapsedSeconds += 1;
      timer.set(t);
    }
  }

  //async function periodicMinute() {
  //  await refresh();
  //}

  async function updateDescription(evt: CustomEvent) {
    //console.log("new description #" + timerId + ": " + evt.detail);
    // do not await
    updateTimer(timerId, evt.detail);
  }

  async function onCreateActivity() {
    let now = moment().startOf("day").local().toDate();
    let year = now.getFullYear();
    let authUser = get(user);
    let _userResp: IApiResponse<IPortalUserDTO> = await PortalUserService.fetchPortalUser(authUser.oid);
    let _us = _userResp.payload;
    let _applyUserRevenue = authUser != null ? authUser.regionSettings.applyUserRevenue : false;
    let act = <IActivity>{ oid: 0, date: now, year: year, portalUserId: authUser.oid, hourlyLaborCost: _us.hourlyCost, title: "" };
    // set timer description as title
    let tim: IUserTimerStatusDTO = get(timer);

    let ss = tim.elapsedSeconds;
    let hh = Math.floor(ss / (60 * 60));
    ss -= hh * 60 * 60;
    let mm = Math.floor(ss / 60);
    ss -= mm * 60;
    if (ss > 0) mm += 1;

    // at least one minute
    if (hh == 0 && mm == 0) mm = 1;

    act.title = tim.description;
    act.hours = hh;
    act.minutes = mm;
    act.customerId = null;
    act.dossierId = null;
    act.activityTypeId = 0;

    if (_applyUserRevenue === 1) {
      act.hourlyRevenue = _us.hourlyRevenue;
    }

    createTimerActivity(timerId, act);
  }

  // main

  $: displayTime = formatTime($timer);

  onMount(init);
  onDestroy(halt);
</script>

{#if !!timerId}
  <div class="box" class:running={$timer.running} class:completed={$timer.completed}>
    <div class="timer">
      <span class="user-timer-time">{displayTime}</span>

      {#if $timer.completed == false}
        <span class="user-timer-icons">
          {#if $timer.running}
            <i class="material-icons timer-icon" on:click={pause}>pause</i>
          {:else}
            <i class="material-icons timer-icon" on:click={play}>play_arrow</i>
          {/if}
          <i class="material-icons timer-icon" on:click={stop}>stop</i>
        </span>
      {:else}
        <div class="timer-command-button">
          {#if !$timer.activityId}
            <Button klass="is-secondary is-small" on:click={(_) => onCreateActivity()}>Crea attività</Button>
          {:else}
            <Button klass="is-primary is-small" on:click={(_) => navigate("/activity/" + $timer.activityId)}>Visualizza attività</Button>
          {/if}
        </div>
        <div class="timer-command-button">
          <Button klass="is-danger is-small" on:click={(_) => deleteTimer(timerId)}>Elimina timer</Button>
        </div>
      {/if}
    </div>
    <div class="timer-description">
      <DebouncedInput name="" placeholder="" small={true} disabled={$timer.completed} bind:value={$timer.description} on:debounced={updateDescription} />
    </div>
  </div>
{/if}

<style>
  .timer {
    display: flex;
    flex-direction: row;
  }
  .user-timer-time {
    font-size: 130%;
    margin-right: 10px;
    display: inline-block;
  }
  .user-timer-icons {
    display: inline-block;
  }
  .timer-icon {
    cursor: pointer;
  }
  .running {
    background-color: #ffffe0;
  }
  .completed {
    background-color: #99edce;
  }
  .timer-description {
    margin-top: 2px;
  }
  .timer-command-button {
    margin-left: 2rem;
  }
</style>
